/**
 * Static file name patterns that are always accepted for signature pages.
 * These are the official CRA-ARC signature page file names.
 */
export const STATIC_SIGNATURE_PAGE_PATTERNS = [
    'CRA-ARC-Page-de-signature.pdf',
    'CRA-ARC-Signature-Page.pdf',
    'CRA-ARC-Signatue-Page.pdf'
];

/**
 * Generates dynamic file name patterns based on the client's first name.
 * @param {string} clientFirstName - The first name of the client
 * @returns {string[]} Array of accepted file name patterns
 */
export const getDynamicSignaturePagePatterns = (clientFirstName) => {
    if (!clientFirstName) {
        return [];
    }
    
    return [
        `${clientFirstName}_SignaturePage.pdf`,
        `${clientFirstName}_Page-de-signature.pdf`
    ];
};

/**
 * Gets all accepted signature page file name patterns for a given client.
 * @param {string} clientFirstName - The first name of the client
 * @returns {string[]} Array of all accepted file name patterns
 */
export const getAllAcceptedPatterns = (clientFirstName) => {
    return [
        ...STATIC_SIGNATURE_PAGE_PATTERNS,
        ...getDynamicSignaturePagePatterns(clientFirstName)
    ];
};

/**
 * Validates whether a file name matches the expected signature page naming convention.
 * Comparison is case-insensitive.
 * 
 * @param {string} fileName - The uploaded file's name
 * @param {string} clientFirstName - The first name of the client (Primary or Spouse)
 * @returns {boolean} True if the file name matches an accepted pattern
 */
export const isValidSignaturePageFileName = (fileName, clientFirstName) => {
    if (!fileName) {
        return false;
    }

    const allPatterns = getAllAcceptedPatterns(clientFirstName);
    const upperFileName = fileName.toUpperCase();
    
    return allPatterns.some(pattern => 
        upperFileName === pattern.toUpperCase()
    );
};

/**
 * Gets the expected signature page file name for a client.
 * This is the primary expected format: {ClientFirstName}_SignaturePage.pdf
 * 
 * @param {string} clientFirstName - The first name of the client
 * @returns {string} The expected signature page file name
 */
export const getExpectedSignaturePageFileName = (clientFirstName) => {
    if (!clientFirstName) {
        return 'CRA-ARC-Signature-Page.pdf';
    }
    return `${clientFirstName}_SignaturePage.pdf`;
};

/**
 * Checks if an uploaded file matches the signature page naming convention.
 * Used to determine whether to show a warning after file upload.
 * 
 * @param {Object[]} uploadedFiles - Array of uploaded file objects with fileName property
 * @param {string} clientFirstName - The first name of the client
 * @returns {{ isValid: boolean, matchedFile: Object|null }} Validation result
 */
export const validateUploadedSignaturePages = (uploadedFiles, clientFirstName) => {
    if (!uploadedFiles || uploadedFiles.length === 0) {
        return { isValid: false, matchedFile: null };
    }

    for (const file of uploadedFiles) {
        if (isValidSignaturePageFileName(file.fileName, clientFirstName)) {
            return { isValid: true, matchedFile: file };
        }
    }

    return { isValid: false, matchedFile: null };
};
